import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import type { RootState } from "../store/store";
import { getAuthPayload } from "../utils/authToken";
import { getMeApi, MeResponse } from "../views/auth/authApi";

/**
 * Identificação do usuário do staff logado, para saudação e menu.
 *
 * O JWT só traz `email` e `userType`; o nome vem de `GET /auth/me`. A resposta
 * fica em cache por token (a Topbar e a Home pedem ao mesmo tempo: uma
 * requisição só) e um token novo, depois de sair e entrar, busca de novo.
 * Enquanto carrega, ou se a rota falhar, cai para a parte local do e-mail —
 * a tela nunca espera por isso nem mostra erro.
 */
const cache = new Map<string, Promise<MeResponse | null>>();

const fetchMe = (token: string) => {
  let pending = cache.get(token);
  if (!pending) {
    pending = getMeApi(token).catch(() => {
      // Falha não fica em cache: a próxima tela tenta de novo.
      cache.delete(token);
      return null;
    });
    cache.set(token, pending);
  }
  return pending;
};

const PARTICULAS = new Set(["da", "de", "do", "das", "dos", "e"]);

/** "VICTOR DE AGUIAR" → "Victor de Aguiar". Nome já com caixa mista fica como está. */
const normalizeName = (name: string) => {
  if (name !== name.toUpperCase()) return name;
  return name
    .toLowerCase()
    .split(/\s+/)
    .map((w, i) => (i > 0 && PARTICULAS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
};

const initialsOf = (text: string) => {
  const parts = text.split(/[\s._-]+/).filter((p) => p && !PARTICULAS.has(p.toLowerCase()));
  const initials = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : text.slice(0, 2);
  return initials.toUpperCase();
};

export type StaffProfile = {
  /** Nome completo; `null` até o `/auth/me` responder. */
  fullName: string | null;
  /** Primeiro nome, para "Boa noite, Victor". `null` até o `/auth/me` responder. */
  firstName: string | null;
  /** O que mostrar como identificação: o nome, ou a parte local do e-mail. */
  display: string;
  initials: string;
  userType: string;
};

export function useStaffProfile(): StaffProfile {
  const token = useSelector((s: RootState) => s.auth.accessToken);
  const payload = useMemo(() => getAuthPayload(token), [token]);
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    if (!token) {
      setMe(null);
      return;
    }

    let active = true;
    fetchMe(token).then((data) => {
      if (active) setMe(data);
    });
    return () => {
      active = false;
    };
  }, [token]);

  return useMemo(() => {
    const rawName = me?.nomeCompleto?.trim();
    const fullName = rawName ? normalizeName(rawName) : null;
    const emailLocal = String(payload?.email ?? "").split("@")[0].trim();
    const display = fullName ?? emailLocal;

    return {
      fullName,
      firstName: fullName ? fullName.split(/\s+/)[0] : null,
      display,
      initials: display ? initialsOf(display) : "",
      userType: String(payload?.userType ?? "").trim().toUpperCase(),
    };
  }, [me, payload]);
}
